import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { sellerAPI, pointOfSaleAPI } from '../services/api';
import { Users, Plus, ArrowLeft, Edit2, Trash2, DollarSign, Eye, EyeOff, Printer } from 'lucide-react';
import { latinAmericaCountries, formatPhoneNumber, validatePhoneNumber } from '../data/countries';
import { Alert } from '../components/Modal';
import { getRouterColor, getRouterName } from '../utils/routerColors';
import api from '../services/api';

const SellersPage = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState('listar');
  const [sellers, setSellers] = useState([]);
  const [pointsOfSale, setPointsOfSale] = useState([]);
  const [routers, setRouters] = useState([]);
  const [selectedRouterFilter, setSelectedRouterFilter] = useState('all');
  const [showSuspended, setShowSuspended] = useState(false); // DEPRECATED - usar selectedStatusFilter
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('active'); // 'active' | 'suspended' | 'all'
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    country_code: 'MX',
    password: '',
    seller_type: 'acumulativo',
    pos_id: '',
    commission_percentage: '20',
    initial_balance: '',
    direct_print: false,
    qr_print: true
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [alert, setAlert] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [balanceAction, setBalanceAction] = useState(''); // 'add' o 'withdraw'
  const [balanceAmount, setBalanceAmount] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    phone: '',
    pos_id: '',
    commission_percentage: '',
    direct_print: false,
    qr_print: true,
    password: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [sellersRes, posRes, routersRes] = await Promise.all([
        sellerAPI.getAll(),
        pointOfSaleAPI.getAll(),
        api.get('/api/routers')
      ]);
      setSellers(sellersRes.data);
      setPointsOfSale(posRes.data);
      setRouters(routersRes.data);
      
      if (posRes.data.length > 0) {
        setFormData(prev => ({ ...prev, pos_id: posRes.data[0].id }));
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      // Validaciones
      if (!formData.name || formData.name.trim() === '') {
        setError('Debe especificar un nombre');
        setSubmitting(false);
        return;
      }

      if (!validatePhoneNumber(formData.phone, formData.country_code)) {
        const country = latinAmericaCountries.find(c => c.code === formData.country_code);
        setError(`El teléfono debe tener ${country?.phoneLength} dígitos para ${country?.name}`);
        setSubmitting(false);
        return;
      }

      if (!formData.password || formData.password.length < 4) {
        setError('La contraseña debe tener al menos 4 caracteres');
        setSubmitting(false);
        return;
      }

      if (!formData.pos_id) {
        setError('Debe seleccionar un punto de venta');
        setSubmitting(false);
        return;
      }

      if (!formData.commission_percentage || parseFloat(formData.commission_percentage) < 0) {
        setError('Debe especificar una comisión válida');
        setSubmitting(false);
        return;
      }

      if (formData.seller_type === 'por_saldo' && (!formData.initial_balance || parseFloat(formData.initial_balance) <= 0)) {
        setError('Debe especificar un saldo inicial válido para vendedores tipo "Por Saldo"');
        setSubmitting(false);
        return;
      }

      const sellerPayload = {
        name: formData.name,
        phone: formData.phone,
        country_code: formData.country_code,
        password: formData.password,
        seller_type: formData.seller_type,
        pos_id: formData.pos_id,
        commission_percentage: parseFloat(formData.commission_percentage),
        initial_balance: formData.seller_type === 'por_saldo' ? parseFloat(formData.initial_balance) : 0,
        direct_print: formData.direct_print,
        qr_print: formData.qr_print
      };

      await sellerAPI.create(sellerPayload);
      
      // Resetear formulario
      setFormData({
        name: '',
        phone: '',
        country_code: 'MX',
        password: '',
        seller_type: 'acumulativo',
        pos_id: pointsOfSale[0]?.id || '',
        commission_percentage: '20',
        initial_balance: '',
        direct_print: false,
        qr_print: true
      });
      
      setActiveTab('listar');
      loadData();
      
      setAlert({
        type: 'success',
        title: 'Vendedor Creado',
        message: 'El vendedor ha sido creado exitosamente y ya puede iniciar sesión en su panel.'
      });
    } catch (err) {
      console.error('Error creating seller:', err);
      let errorMessage = 'Error al crear vendedor';
      
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        // Si detail es un array de errores de validación de Pydantic
        if (Array.isArray(detail)) {
          errorMessage = detail.map(e => `${e.loc?.join('.') || 'Campo'}: ${e.msg}`).join('; ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else {
          errorMessage = JSON.stringify(detail);
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (sellerId, sellerName) => {
    try {
      const seller = sellers.find(s => s.id === sellerId);
      const isReactivating = !seller.is_active;
      
      // Si está reactivando, hacerlo directamente sin confirmación
      if (isReactivating) {
        const response = await sellerAPI.delete(sellerId);
        loadData();
        setAlert({
          type: 'success',
          title: 'Vendedor Reactivado',
          message: `El vendedor "${sellerName}" ha sido reactivado exitosamente.`
        });
        return;
      }
      
      // Si está suspendiendo, PRIMERO pedir confirmación
      const confirmMessage = `¿Está seguro de SUSPENDER al vendedor "${sellerName}"?\n\n` +
        `⚠️ No podrá generar fichas mientras esté suspendido.\n` +
        `✅ Su saldo, fichas y ventas se conservarán.`;
      
      if (!window.confirm(confirmMessage)) {
        // Si cancela, NO hacer nada
        return;
      }
      
      // Si confirma, AHORA SÍ ejecutar la suspensión
      const response = await sellerAPI.delete(sellerId);
      loadData();
      setAlert({
        type: 'success',
        title: 'Vendedor Suspendido',
        message: response.data.message || `El vendedor "${sellerName}" ha sido suspendido.`
      });
    } catch (err) {
      console.error('Error:', err);
      setAlert({
        type: 'error',
        title: 'Error',
        message: 'No se pudo completar la operación. Intenta nuevamente.'
      });
    }
  };

  const handleAddBalance = (seller) => {
    setSelectedSeller(seller);
    setBalanceAction('add');
    setBalanceAmount('');
    setShowBalanceModal(true);
  };

  const handleWithdrawBalance = (seller) => {
    setSelectedSeller(seller);
    setBalanceAction('withdraw');
    setBalanceAmount('');
    setShowBalanceModal(true);
  };

  const handleEdit = (seller) => {
    setSelectedSeller(seller);
    setEditFormData({
      name: seller.name,
      phone: seller.phone,
      pos_id: seller.pos_id,
      commission_percentage: seller.commission_percentage.toString(),
      direct_print: seller.direct_print,
      qr_print: seller.qr_print,
      password: ''
    });
    setShowEditModal(true);
  };

  const handleUpdateSeller = async (e) => {
    e.preventDefault();
    
    try {
      // Validaciones
      if (!editFormData.name || editFormData.name.trim() === '') {
        setAlert({
          type: 'error',
          title: 'Campo Requerido',
          message: 'Debe especificar un nombre para el vendedor.'
        });
        return;
      }

      if (!validatePhoneNumber(editFormData.phone, 'MX')) {
        const country = latinAmericaCountries.find(c => c.code === 'MX');
        setAlert({
          type: 'error',
          title: 'Teléfono Inválido',
          message: `El teléfono debe tener ${country?.phoneLength} dígitos para ${country?.name}.`
        });
        return;
      }

      if (!editFormData.pos_id) {
        setAlert({
          type: 'error',
          title: 'Campo Requerido',
          message: 'Debe seleccionar un punto de venta.'
        });
        return;
      }

      if (!editFormData.commission_percentage || parseFloat(editFormData.commission_percentage) < 0) {
        setAlert({
          type: 'error',
          title: 'Comisión Inválida',
          message: 'Debe especificar una comisión válida.'
        });
        return;
      }

      const updatePayload = {
        name: editFormData.name.toUpperCase(),
        phone: editFormData.phone,
        pos_id: editFormData.pos_id,
        commission_percentage: parseFloat(editFormData.commission_percentage),
        direct_print: editFormData.direct_print,
        qr_print: editFormData.qr_print
      };
      
      // Agregar password solo si se proporcionó
      if (editFormData.password && editFormData.password.trim() !== '') {
        updatePayload.password = editFormData.password;
      }

      await sellerAPI.update(selectedSeller.id, updatePayload);
      
      setShowEditModal(false);
      setSelectedSeller(null);
      loadData();
      setAlert({
        type: 'success',
        title: 'Vendedor Actualizado',
        message: 'Los datos del vendedor han sido actualizados correctamente.'
      });
    } catch (err) {
      console.error('Error updating seller:', err);
      setAlert({
        type: 'error',
        title: 'Error al Actualizar',
        message: err.response?.data?.detail || 'No se pudo actualizar el vendedor. Intenta nuevamente.'
      });
    }
  };

  const handleGenerateVouchersForSeller = (seller) => {
    // Navegar a la página de vouchers con el seller pre-seleccionado
    navigate('/vouchers-admin', { 
      state: { 
        generationType: 'seller',
        preselectedSeller: seller.id,
        sellerName: seller.name
      } 
    });
  };


  const handleBalanceSubmit = async () => {
    if (!balanceAmount || parseFloat(balanceAmount) <= 0) {
      setAlert({
        type: 'error',
        title: 'Monto Inválido',
        message: 'Por favor ingrese un monto válido mayor a cero.'
      });
      return;
    }

    try {
      const amount = parseFloat(balanceAmount);

      if (balanceAction === 'add') {
        // Agregar saldo (solo para "por_saldo")
        const response = await sellerAPI.addBalance(selectedSeller.id, amount);
        setAlert({
          type: 'success',
          title: 'Saldo Agregado',
          message: `La transacción se completó exitosamente.\nMonto: $${response.data.amount_added.toFixed(2)} MXN\nComisión (${selectedSeller.commission_percentage}%): $${response.data.commission.toFixed(2)} MXN\nTotal agregado: $${response.data.total_added.toFixed(2)} MXN\nNuevo saldo: $${response.data.new_balance.toFixed(2)} MXN`
        });
      } else if (balanceAction === 'withdraw') {
        // Retirar saldo (para "acumulativo")
        const response = await sellerAPI.withdrawBalance(selectedSeller.id, amount);
        
        setAlert({
          type: 'success',
          title: 'Retiro Realizado',
          message: `La transacción se completó exitosamente.\nVentas totales: $${response.data.amount_withdrawn.toFixed(2)} MXN\nComisión vendedor (${selectedSeller.commission_percentage}%): $${response.data.seller_commission.toFixed(2)} MXN\nAdmin recibe: $${response.data.admin_receives.toFixed(2)} MXN\nNuevo saldo: $${response.data.new_balance.toFixed(2)} MXN`
        });
      }

      setShowBalanceModal(false);
      setSelectedSeller(null);
      setBalanceAmount('');
      loadData();
    } catch (err) {
      console.error('Error processing balance:', err);
      setAlert({
        type: 'error',
        title: 'Error en Transacción',
        message: 'No se pudo procesar la transacción de saldo. Intenta nuevamente.'
      });
    }
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDark ? 'bg-gray-900' : 'bg-gray-100'
    }`}>
      <header className={`shadow-sm transition-colors duration-300 ${
        isDark ? 'bg-gray-800 border-b border-gray-700' : 'bg-white'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className={`p-2 rounded-lg transition-colors ${
                  isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-900'
                }`}
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Vendedores</h1>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Gestiona tu equipo de ventas</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className={`flex gap-2 mb-6 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            onClick={() => setActiveTab('listar')}
            style={{
              color: activeTab === 'listar' 
                ? '#2563eb' 
                : isDark ? '#d1d5db' : '#4b5563'
            }}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 ${
              activeTab === 'listar'
                ? 'border-blue-600'
                : 'border-transparent hover:opacity-80'
            }`}
          >
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Ver Vendedores
            </div>
          </button>
          <button
            onClick={() => setActiveTab('crear')}
            style={{
              color: activeTab === 'crear' 
                ? '#2563eb' 
                : isDark ? '#d1d5db' : '#4b5563'
            }}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 ${
              activeTab === 'crear'
                ? 'border-blue-600'
                : 'border-transparent hover:opacity-80'
            }`}
          >
            <div className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Crear Vendedor
            </div>
          </button>
        </div>

        {activeTab === 'crear' && (
          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-8`}>
            <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>Crear Nuevo Vendedor</h2>
            
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Tipo de Vendedor *
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, seller_type: 'acumulativo', initial_balance: '' })}
                      className={`p-4 border-2 rounded-lg transition-all ${
                        formData.seller_type === 'acumulativo'
                          ? 'border-blue-600 bg-blue-50'
                          : 'bg-white border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <p className="font-semibold text-gray-900">ACUMULATIVO</p>
                      <p className="text-xs mt-1 text-gray-600">Empieza en $0, cada venta suma al saldo</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, seller_type: 'por_saldo' })}
                      className={`p-4 border-2 rounded-lg transition-all ${
                        formData.seller_type === 'por_saldo'
                          ? 'border-blue-600 bg-blue-50'
                          : 'bg-white border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <p className="font-semibold text-gray-900">POR SALDO</p>
                      <p className="text-xs mt-1 text-gray-600">Se le asigna saldo, cada venta resta</p>
                    </button>
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Nombre *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value.toUpperCase() })}
                    className="w-full px-4 py-3 rounded-lg border-2 bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="JUAN PÉREZ"
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>País *</label>
                  <select
                    value={formData.country_code}
                    onChange={(e) => setFormData({ ...formData, country_code: e.target.value, phone: '' })}
                    className="w-full px-4 py-3 rounded-lg border-2 bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {latinAmericaCountries.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.flag} {country.name} ({country.dialCode})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Teléfono *
                  </label>
                  <div className="flex gap-2">
                    <div className="flex items-center px-4 py-3 rounded-lg border-2 font-semibold bg-gray-100 border-gray-300 text-gray-700">
                      {latinAmericaCountries.find(c => c.code === formData.country_code)?.dialCode}
                    </div>
                    <input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, '');
                        const country = latinAmericaCountries.find(c => c.code === formData.country_code);
                        if (digits.length <= (country?.phoneLength || 10)) {
                          setFormData({ ...formData, phone: digits });
                        }
                      }}
                      className="flex-1 px-4 py-3 rounded-lg border-2 bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={latinAmericaCountries.find(c => c.code === formData.country_code)?.phoneFormat.replace(/X/g, '0')}
                    />
                  </div>
                  <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Formato: {latinAmericaCountries.find(c => c.code === formData.country_code)?.phoneFormat}
                  </p>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Contraseña *</label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      required
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className={`w-full px-4 py-3 pr-12 rounded-lg border-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        isDark 
                          ? 'bg-gray-700/50 border-gray-600 text-white placeholder-gray-400' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Para iniciar sesión como vendedor</p>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Punto de Venta *</label>
                  <select
                    required
                    value={formData.pos_id}
                    onChange={(e) => setFormData({ ...formData, pos_id: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border-2 bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Selecciona un TPV</option>
                    {pointsOfSale.map((pos) => (
                      <option key={pos.id} value={pos.id}>{pos.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Comisión (%) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    required
                    value={formData.commission_percentage}
                    onChange={(e) => setFormData({ ...formData, commission_percentage: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border-2 bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="20"
                  />
                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Se calcula sobre las ventas</p>
                </div>

                {formData.seller_type === 'por_saldo' && (
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Saldo Inicial *</label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        required={formData.seller_type === 'por_saldo'}
                        value={formData.initial_balance}
                        onChange={(e) => setFormData({ ...formData, initial_balance: e.target.value })}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="100.00"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Se le sumará la comisión ({formData.commission_percentage}%)
                      {formData.initial_balance && formData.commission_percentage && (
                        <span className="font-semibold text-green-600">
                          {' '}→ Saldo total: ${(parseFloat(formData.initial_balance) * (1 + parseFloat(formData.commission_percentage) / 100)).toFixed(2)}
                        </span>
                      )}
                    </p>
                  </div>
                )}

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-3">Opciones de Impresión</label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.direct_print}
                        onChange={(e) => setFormData({ ...formData, direct_print: e.target.checked })}
                        className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-900">Impresión Directa</span>
                        <p className="text-xs text-gray-500">Enviar a impresora Bluetooth automáticamente</p>
                      </div>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.qr_print}
                        onChange={(e) => setFormData({ ...formData, qr_print: e.target.checked })}
                        className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-900">Código QR en Ficha</span>
                        <p className="text-xs text-gray-500">Mostrar QR en las fichas generadas</p>
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex gap-4 pt-6">
                <button
                  type="button"
                  onClick={() => {
                    setFormData({
                      name: '',
                      phone: '',
                      password: '',
                      seller_type: 'acumulativo',
                      pos_id: pointsOfSale[0]?.id || '',
                      commission_percentage: '20',
                      initial_balance: '',
                      direct_print: false,
                      qr_print: true
                    });
                    setError('');
                  }}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                >
                  Limpiar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 font-semibold"
                >
                  {submitting ? 'Creando...' : 'Crear Vendedor'}
                </button>
              </div>
            </form>
          </div>
        )}

        {activeTab === 'listar' && (
          <>
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : sellers.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No hay vendedores</h3>
                <p className="text-gray-600 mb-6">Crea tu primer vendedor</p>
                <button
                  onClick={() => setActiveTab('crear')}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Crear Vendedor
                </button>
              </div>
            ) : (
              <>
                {/* Tabs de filtrado por estado */}
                <div className="mb-6 flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedStatusFilter('active')}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      selectedStatusFilter === 'active'
                        ? 'bg-green-600 text-white'
                        : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    🟢 Activos ({sellers.filter(s => s.is_active !== false).length})
                  </button>
                  <button
                    onClick={() => setSelectedStatusFilter('suspended')}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      selectedStatusFilter === 'suspended'
                        ? 'bg-orange-600 text-white'
                        : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    🔴 Suspendidos ({sellers.filter(s => s.is_active === false).length})
                  </button>
                  <button
                    onClick={() => setSelectedStatusFilter('all')}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      selectedStatusFilter === 'all'
                        ? 'bg-blue-600 text-white'
                        : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    📋 Todos ({sellers.length})
                  </button>
                </div>
                
                {/* Tabs de filtrado por router */}
                {routers.length > 1 && (
                  <div className="mb-6 flex flex-wrap gap-2">
                    <button
                      onClick={() => setSelectedRouterFilter('all')}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        selectedRouterFilter === 'all'
                          ? 'bg-blue-600 text-white'
                          : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Todos ({sellers.length})
                    </button>
                    {routers.map((router) => {
                      const routerSellers = sellers.filter(s => {
                        const pos = pointsOfSale.find(p => p.id === s.pos_id);
                        return pos && pos.router_id === router.id;
                      });
                      const colors = getRouterColor(router.id, routers);
                      return (
                        <button
                          key={router.id}
                          onClick={() => setSelectedRouterFilter(router.id)}
                          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                            selectedRouterFilter === router.id
                              ? `${colors.badge} text-white`
                              : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {router.name} ({routerSellers.length})
                        </button>
                      );
                    })}
                  </div>
                )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {sellers
                  .filter(seller => {
                    const pos = pointsOfSale.find(p => p.id === seller.pos_id);
                    const matchesRouter = selectedRouterFilter === 'all' || (pos && pos.router_id === selectedRouterFilter);
                    // Filtro por estado
                    const matchesStatus = selectedStatusFilter === 'all' 
                      ? true 
                      : selectedStatusFilter === 'active' 
                        ? seller.is_active !== false 
                        : seller.is_active === false;
                    return matchesRouter && matchesStatus;
                  })
                  .map((seller) => {
                  const pos = pointsOfSale.find(p => p.id === seller.pos_id);
                  const colors = pos ? getRouterColor(pos.router_id, routers) : getRouterColor(null, routers);
                  const routerName = pos ? getRouterName(pos.router_id, routers) : 'Sin router';
                  
                  return (
                    <div key={seller.id} className={`bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden border-2 ${colors.border} ${!seller.is_active ? 'opacity-50' : ''}`} style={!seller.is_active ? {filter: 'grayscale(50%)'} : {}}>
                      {/* Badge de suspendido */}
                      {!seller.is_active && (
                        <div className="absolute top-2 right-2 z-10 bg-orange-500 text-white text-xs px-2 py-1 rounded-full font-semibold">
                          SUSPENDIDO
                        </div>
                      )}
                      {/* Badge de router */}
                      <div className={`${colors.badge} text-white text-xs font-semibold px-3 py-1`}>
                        {routerName}
                      </div>
                      
                      <div className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 ${colors.bg} rounded-lg`}>
                              <Users className={`w-6 h-6 ${colors.text}`} />
                            </div>
                            <div>
                              <h3 className="text-xl font-bold text-gray-900">{seller.name}</h3>
                              <p className="text-sm text-gray-600">{seller.phone}</p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="space-y-2 mb-4">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Tipo:</span>
                            <span className="font-semibold capitalize">{seller.seller_type === 'acumulativo' ? 'Acumulativo' : 'Por Saldo'}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Saldo:</span>
                            <span className="font-semibold text-green-600">${seller.current_balance.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Comisión:</span>
                            <span className="font-semibold">{seller.commission_percentage}%</span>
                          </div>
                          {pos && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">TPV:</span>
                              <span className="font-semibold text-xs">{pos.name}</span>
                            </div>
                          )}
                        </div>

                        <div className="flex gap-2 text-xs text-gray-600 mb-4">
                          {seller.direct_print && (
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">Impresión Directa</span>
                          )}
                          {seller.qr_print && (
                            <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">QR</span>
                          )}
                        </div>

                        <div className="space-y-2 pt-4 border-t border-gray-200">
                          <button
                            onClick={() => handleGenerateVouchersForSeller(seller)}
                            className={`w-full flex items-center justify-center gap-2 px-4 py-2 ${colors.bg} ${colors.text} rounded-lg hover:opacity-80 transition-colors text-sm font-semibold`}
                          >
                            <Printer className="w-4 h-4" />
                            Generar Fichas
                          </button>
                          <button
                            onClick={() => handleEdit(seller)}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-sm font-semibold"
                          >
                            <Edit2 className="w-4 h-4" />
                            Editar
                          </button>
                          {seller.seller_type === 'por_saldo' && (
                            <button
                              onClick={() => handleAddBalance(seller)}
                              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors text-sm font-semibold"
                            >
                              <DollarSign className="w-4 h-4" />
                              Abonar
                            </button>
                          )}
                          {seller.seller_type === 'acumulativo' && (
                            <button
                              onClick={() => handleWithdrawBalance(seller)}
                              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-yellow-50 text-yellow-700 rounded-lg hover:bg-yellow-100 transition-colors text-sm font-semibold"
                            >
                              <DollarSign className="w-4 h-4" />
                              Retirar
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(seller.id, seller.name || seller.phone)}
                            className={`w-full flex items-center justify-center gap-2 px-4 py-2 ${
                              !seller.is_active 
                                ? 'bg-green-50 text-green-700 hover:bg-green-100' 
                                : 'bg-orange-50 text-orange-700 hover:bg-orange-100'
                            } rounded-lg transition-colors text-sm font-semibold`}
                          >
                            <Trash2 className="w-4 h-4" />
                            {!seller.is_active ? 'Reactivar' : 'Suspender'}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Balance Modal */}
      {showBalanceModal && selectedSeller && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-xl font-bold mb-4">
                {balanceAction === 'add' ? '💰 Abonar Saldo' : '💵 Retirar Saldo'}
              </h3>
              
              <div className="mb-6 p-6 bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl border border-blue-200">
                <p className="text-sm text-gray-600 mb-1">{selectedSeller.name}</p>
                <p className="text-3xl font-bold text-gray-900">${selectedSeller.current_balance.toFixed(2)}</p>
                <p className="text-xs text-gray-500 mt-1">Saldo Actual</p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Monto a retirar *
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={balanceAmount}
                    onChange={(e) => setBalanceAmount(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0.00"
                    autoFocus
                  />
                </div>
                
                {balanceAmount && parseFloat(balanceAmount) > 0 && balanceAction === 'withdraw' && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Monto a retirar:</span>
                      <span className="font-semibold">${parseFloat(balanceAmount).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Comisión ({selectedSeller.commission_percentage}%):</span>
                      <span className="font-semibold text-red-600">-${(parseFloat(balanceAmount) * selectedSeller.commission_percentage / 100).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between pt-3 border-t-2 border-gray-300">
                      <span className="font-bold text-gray-900">Me entrega:</span>
                      <span className="font-bold text-green-600 text-lg">${(parseFloat(balanceAmount) * (1 - selectedSeller.commission_percentage / 100)).toFixed(2)}</span>
                    </div>
                  </div>
                )}
                
                {balanceAmount && parseFloat(balanceAmount) > 0 && balanceAction === 'add' && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Monto:</span>
                      <span className="font-semibold">${parseFloat(balanceAmount).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Comisión ({selectedSeller.commission_percentage}%):</span>
                      <span className="font-semibold text-green-600">+${(parseFloat(balanceAmount) * selectedSeller.commission_percentage / 100).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between pt-3 border-t-2 border-gray-300">
                      <span className="font-bold text-gray-900">Total a agregar:</span>
                      <span className="font-bold text-green-600 text-lg">${(parseFloat(balanceAmount) * (1 + selectedSeller.commission_percentage / 100)).toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowBalanceModal(false);
                    setSelectedSeller(null);
                    setBalanceAmount('');
                  }}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleBalanceSubmit}
                  disabled={!balanceAmount || parseFloat(balanceAmount) <= 0}
                  className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 font-semibold"
                >
                  {balanceAction === 'add' ? 'Abonar' : 'Retirar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Seller Modal */}
      {showEditModal && selectedSeller && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-2xl font-bold mb-6">✏️ Editar Vendedor</h3>
              
              <form onSubmit={handleUpdateSeller} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre *
                    </label>
                    <input
                      type="text"
                      required
                      value={editFormData.name}
                      onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value.toUpperCase() })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="JUAN PÉREZ"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Teléfono *
                    </label>
                    <input
                      type="tel"
                      required
                      maxLength="15"
                      value={editFormData.phone}
                      onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value.replace(/\D/g, '') })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="5512345678"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Punto de Venta *
                    </label>
                    <select
                      value={editFormData.pos_id}
                      onChange={(e) => setEditFormData({ ...editFormData, pos_id: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    >
                      <option value="">Seleccionar TPV</option>
                      {pointsOfSale.map((pos) => (
                        <option key={pos.id} value={pos.id}>
                          {pos.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Comisión (%) *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      required
                      value={editFormData.commission_percentage}
                      onChange={(e) => setEditFormData({ ...editFormData, commission_percentage: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="20"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="flex items-center gap-3 p-4 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="checkbox"
                      checked={editFormData.direct_print}
                      onChange={(e) => setEditFormData({ ...editFormData, direct_print: e.target.checked })}
                      className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <div>
                      <p className="font-semibold text-gray-900">Impresión Directa Bluetooth</p>
                      <p className="text-xs text-gray-600">Imprime automáticamente las fichas generadas</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 p-4 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="checkbox"
                      checked={editFormData.qr_print}
                      onChange={(e) => setEditFormData({ ...editFormData, qr_print: e.target.checked })}
                      className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <div>
                      <p className="font-semibold text-gray-900">Mostrar Código QR en Fichas</p>
                      <p className="text-xs text-gray-600">Incluye QR para acceso rápido</p>
                    </div>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nueva Contraseña (Opcional)
                  </label>
                  <input
                    type="password"
                    value={editFormData.password}
                    onChange={(e) => setEditFormData({ ...editFormData, password: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Dejar vacío para no cambiar"
                    minLength="4"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Mínimo 4 caracteres. Dejar vacío para mantener contraseña actual.
                  </p>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800">
                    <strong>Nota:</strong> No se puede cambiar el tipo de vendedor desde aquí. El saldo actual se mantiene: <strong>${selectedSeller.current_balance.toFixed(2)}</strong>
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setSelectedSeller(null);
                    }}
                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                  >
                    Guardar Cambios
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Alert Component */}
      {alert && (
        <Alert
          type={alert.type}
          title={alert.title}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}
    </div>
  );
};

export default SellersPage;
